import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Link as RouterLink } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaLink } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import { LinkCalendarEventModal, type OutlookEvent } from '../components/LinkCalendarEventModal';
import { directSupabase } from '../lib/supabaseClient';
import { getGraphClient } from '../utils/graphService';
import { todayInstallDateOnly } from './project-workspace/jobCardInstallDate';
import {
  EVENT_TYPE_LABELS,
  addDays,
  diaryRows,
  diaryWhen,
  mondayOf,
  type DiaryEvent,
  type DiaryLink,
} from './outlookLinks.model';

const WEEK_OPTIONS = [1, 2, 4];

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

async function loadDiary(from: string, to: string): Promise<DiaryEvent[]> {
  const client = await getGraphClient();
  const events: DiaryEvent[] = [];
  let request: any = client
    .api('/me/calendarView')
    .header('Prefer', 'outlook.timezone="Europe/London"')
    .query({ startDateTime: `${from}T00:00:00`, endDateTime: `${to}T00:00:00` })
    .select('id,subject,start,end,isAllDay,location,categories,bodyPreview,type')
    .orderby('start/dateTime')
    .top(250);
  for (let page = 0; page < 10 && request; page += 1) {
    const response = await request.get();
    events.push(...((response.value || []) as DiaryEvent[]));
    request = response['@odata.nextLink'] ? client.api(response['@odata.nextLink']).header('Prefer', 'outlook.timezone="Europe/London"') : null;
  }
  return events;
}

async function loadLinks(eventIds: string[]): Promise<DiaryLink[]> {
  const links: DiaryLink[] = [];
  for (const ids of chunks(eventIds, 80)) {
    const [bookings, cards] = await Promise.all([
      directSupabase
        .from('project_events')
        .select('calendar_event_id, event_type, status, project:projects(id, project_name, customer_name)')
        .in('calendar_event_id', ids),
      (directSupabase as any)
        .from('project_job_cards')
        .select('calendar_event_id, title, project:projects(id, project_name, customer_name)')
        .in('calendar_event_id', ids),
    ]);
    if (bookings.error) throw new Error(bookings.error.message);
    ((bookings.data || []) as any[]).forEach((row) => {
      // A cancelled booking no longer owns its Outlook event
      if (row.status === 'cancelled' || !row.project) return;
      links.push({
        eventId: row.calendar_event_id,
        projectId: String(row.project.id),
        workspace: row.project.customer_name || row.project.project_name || 'Workspace',
        label: EVENT_TYPE_LABELS[row.event_type] || 'Linked',
      });
    });
    ((cards.data || []) as any[]).forEach((row) => {
      if (!row.project) return;
      links.push({
        eventId: row.calendar_event_id,
        projectId: String(row.project.id),
        workspace: row.project.customer_name || row.project.project_name || 'Workspace',
        label: row.title || 'Job card',
      });
    });
  }
  return links;
}

/**
 * Link Outlook Events: a utility for attaching diary events made in Outlook to a workspace.
 * Dates and times belong to job cards and installation bookings, so nothing is edited here.
 */
const WorkCalendarPage: React.FC = () => {
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayInstallDateOnly()));
  const [weeks, setWeeks] = useState(1);
  const [unlinkedOnly, setUnlinkedOnly] = useState(true);
  const [events, setEvents] = useState<DiaryEvent[]>([]);
  const [links, setLinks] = useState<DiaryLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<OutlookEvent | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const rangeEnd = addDays(weekStart, weeks * 7);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const diary = await loadDiary(weekStart, rangeEnd);
      setEvents(diary);
      setLinks(await loadLinks(diary.map((event) => event.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the Outlook diary');
    } finally {
      setLoading(false);
    }
  }, [weekStart, rangeEnd]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => diaryRows(events, links, unlinkedOnly), [events, links, unlinkedOnly]);
  const linkedCount = useMemo(() => new Set(links.map((link) => link.eventId)).size, [links]);
  const rangeLabel = `${new Date(`${weekStart}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${new Date(`${addDays(rangeEnd, -1)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`;

  const openLink = (event: DiaryEvent | null) => {
    setLinking(event as OutlookEvent | null);
    setLinkOpen(true);
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, width: '100%', maxWidth: 1200, mx: 'auto', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography level="h3">Link Outlook Events</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', maxWidth: 720 }}>
            Find diary entries made in Outlook that aren&apos;t attached to a workspace, and link them. Dates and times are managed on job cards and installation bookings, not here.
          </Typography>
        </Box>
        <Button size="sm" startDecorator={<IconWrapper icon={FaLink} />} onClick={() => openLink(null)}>
          Search and link
        </Button>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <IconButton size="sm" variant="outlined" aria-label="Previous" onClick={() => setWeekStart(addDays(weekStart, -7 * weeks))}>
          <IconWrapper icon={FaChevronLeft} />
        </IconButton>
        <Button size="sm" variant="outlined" color="neutral" onClick={() => setWeekStart(mondayOf(todayInstallDateOnly()))}>This week</Button>
        <IconButton size="sm" variant="outlined" aria-label="Next" onClick={() => setWeekStart(addDays(weekStart, 7 * weeks))}>
          <IconWrapper icon={FaChevronRight} />
        </IconButton>
        <Typography level="title-sm" sx={{ mx: 1 }}>{rangeLabel}</Typography>
        <Select size="sm" value={weeks} onChange={(_, value) => value && setWeeks(value)} sx={{ minWidth: 110 }}>
          {WEEK_OPTIONS.map((option) => <Option key={option} value={option}>{option} week{option === 1 ? '' : 's'}</Option>)}
        </Select>
        <Checkbox size="sm" label="Unlinked only" checked={unlinkedOnly} onChange={(event) => setUnlinkedOnly(event.target.checked)} sx={{ ml: 1 }} />
      </Stack>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size="sm" /></Stack>
      ) : (
        <>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
            {events.length} event{events.length === 1 ? '' : 's'} in Outlook · {linkedCount} linked
            {unlinkedOnly ? ' · showing unlinked one-off events (recurring meetings hidden)' : ''}
          </Typography>
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table size="sm" hoverRow sx={{ minWidth: 760, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '26%' }}>When</th>
                  <th style={{ width: '40%' }}>Outlook event</th>
                  <th style={{ width: '34%' }}>Linked to</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ event, links: eventLinks, recurring }) => (
                  <tr key={event.id}>
                    <td><Typography level="body-sm">{diaryWhen(event)}</Typography></td>
                    <td>
                      <Typography level="body-sm" fontWeight="lg">{event.subject || '(no title)'}</Typography>
                      {event.location?.displayName && (
                        <Typography level="body-xs" textColor="neutral.500" noWrap>{event.location.displayName}</Typography>
                      )}
                      {recurring && <Chip size="sm" variant="outlined">Recurring</Chip>}
                    </td>
                    <td>
                      {eventLinks.length ? (
                        <Stack gap={0.5}>
                          {eventLinks.map((link) => (
                            <Stack key={`${link.projectId}-${link.label}`} direction="row" gap={1} alignItems="center" flexWrap="wrap">
                              <Chip size="sm" variant="soft" color="success">{link.label}</Chip>
                              <Typography
                                level="body-sm"
                                component={RouterLink}
                                to={`/projects/${link.projectId}/workspace/implementation`}
                                sx={{ color: 'primary.plainColor' }}
                              >
                                {link.workspace}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Button size="sm" variant="outlined" startDecorator={<IconWrapper icon={FaLink} />} onClick={() => openLink(event)}>
                          Link
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                        {unlinkedOnly ? 'Every one-off event in this period is linked.' : 'No Outlook events in this period.'}
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        </>
      )}

      <LinkCalendarEventModal
        open={linkOpen}
        initialEvent={linking}
        onClose={() => { setLinkOpen(false); setLinking(null); }}
        onSuccess={() => { setLinkOpen(false); setLinking(null); void load(); }}
      />
    </Box>
  );
};

export default WorkCalendarPage;
